import { getFormProps, getInputProps, useForm } from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod/v4'
import { type SEOHandle } from '@nasa-gcn/remix-seo'
import * as E from 'react-email'
import {
	type ActionFunctionArgs,
	data,
	Link,
	type LoaderFunctionArgs,
	type MetaFunction,
	redirect,
	useFetcher,
} from 'react-router'
import { HoneypotInputs } from 'remix-utils/honeypot/react'
import { z } from 'zod'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import { ErrorList, Field } from '#app/components/forms.tsx'
import { Spacer } from '#app/components/spacer.tsx'
import { StatusButton } from '#app/components/ui/status-button.tsx'
import { requireAnonymous } from '#app/utils/auth.server.js'
import { prisma } from '#app/utils/db.server.ts'
import { sendEmail } from '#app/utils/email.server.ts'
import { checkHoneypot } from '#app/utils/honeypot.server.ts'
import { EmailSchema, UsernameSchema } from '#app/utils/user-validation.ts'
import { prepareVerification } from './verify.server.ts'

export const handle: SEOHandle = {
	getSitemapEntries: () => null,
}

const ForgotPasswordSchema = z.object({
	usernameOrEmail: z.union([EmailSchema, UsernameSchema]),
})

export const meta: MetaFunction = () => {
	const title = `Password Recovery for ${import.meta.env.VITE_APP_TITLE}`
	const description =
		'Password Recovery for immersive 360° outdoor exploration — featuring the latest community captures on TrailViewr.com.'

	const url = `https://www.trailviewr.com/forgot-password`

	return [
		{ title },
		{ name: 'description', content: description },
		{ tagName: 'link', rel: 'canonical', href: url },

		{ property: 'og:title', content: title },
		{ property: 'og:description', content: description },
		{ property: 'og:url', content: url },

		{ name: 'twitter:title', content: title },
		{ name: 'twitter:description', content: description },
		{ name: 'twitter:url', content: url },
	]
}
export async function loader({ request }: LoaderFunctionArgs) {
	await requireAnonymous(request)
	return {}
}

export async function action({ request }: ActionFunctionArgs) {
	const formData = await request.formData()
	await checkHoneypot(formData)
	const submission = await parseWithZod(formData, {
		schema: ForgotPasswordSchema.superRefine(async (data, ctx) => {
			const user = await prisma.user.findFirst({
				where: {
					OR: [
						{ email: data.usernameOrEmail },
						{ username: data.usernameOrEmail },
					],
				},
				select: { id: true },
			})
			if (!user) {
				ctx.addIssue({
					path: ['usernameOrEmail'],
					code: z.ZodIssueCode.custom,
					message: 'No user exists with this username or email',
				})
				return
			}
		}),
		async: true,
	})
	if (submission.status !== 'success') {
		return data(
			{ result: submission.reply() },
			{ status: submission.status === 'error' ? 400 : 200 },
		)
	}
	const { usernameOrEmail } = submission.value

	const user = await prisma.user.findFirstOrThrow({
		where: { OR: [{ email: usernameOrEmail }, { username: usernameOrEmail }] },
		select: { email: true, username: true },
	})

	const { verifyUrl, redirectTo, otp } = await prepareVerification({
		period: 10 * 60,
		request,
		type: 'reset-password',
		target: usernameOrEmail,
	})

	const response = await sendEmail({
		to: user.email,
		subject: `${import.meta.env.VITE_APP_TITLE} Password Reset`,
		react: (
			<ForgotPasswordEmail onboardingUrl={verifyUrl.toString()} otp={otp} />
		),
	})

	if (response.status === 'success') {
		return redirect(redirectTo.toString())
	} else {
		return data(
			{ result: submission.reply({ formErrors: [response.error.message] }) },
			{ status: 500 },
		)
	}
}

function ForgotPasswordEmail({
	onboardingUrl,
	otp,
}: {
	onboardingUrl: string
	otp: string
}) {
	return (
		<E.Html lang="en" dir="ltr">
			<E.Container>
				<h1>
					<E.Text>{import.meta.env.VITE_APP_TITLE} Password Reset</E.Text>
				</h1>
				<p>
					<E.Text>
						Here's your verification code: <strong>{otp}</strong>
					</E.Text>
				</p>
				<p>
					<E.Text>Or click the link:</E.Text>
				</p>
				<E.Link href={onboardingUrl}>{onboardingUrl}</E.Link>
			</E.Container>
		</E.Html>
	)
}

export default function ForgotPasswordRoute() {
	const forgotPassword = useFetcher<typeof action>()

	const [form, fields] = useForm({
		id: 'forgot-password-form',
		constraint: getZodConstraint(ForgotPasswordSchema),
		defaultValue: {
			usernameOrEmail: '',
		},
		lastResult: forgotPassword.data?.result,
		onValidate({ formData }) {
			return parseWithZod(formData, { schema: ForgotPasswordSchema })
		},
		shouldRevalidate: 'onBlur',
	})

	return (
		<div className="mx-2 flex h-full flex-col md:mx-4 lg:mx-8">
			<div className="grow">
				<div className="flex min-h-full flex-col justify-center px-3 pt-20 pb-32">
					<div className="mx-auto w-full max-w-md">
						<div className="flex flex-col gap-3 text-center">
							<h1 className="text-h1">Forgot Password</h1>
							<p className="text-body-md text-muted-foreground">
								No worries, we'll send you reset instructions.
							</p>
						</div>
						<Spacer size="xs" />

						<div className="mx-auto w-full max-w-md px-6">
							<forgotPassword.Form method="POST" {...getFormProps(form)}>
								<HoneypotInputs />
								<div>
									<Field
										labelProps={{
											htmlFor: fields.usernameOrEmail.id,
											children: 'Username or Email',
										}}
										inputProps={{
											autoFocus: true,
											...getInputProps(fields.usernameOrEmail, {
												type: 'text',
											}),
										}}
										errors={fields.usernameOrEmail.errors}
									/>
								</div>
								<ErrorList errors={form.errors} id={form.errorId} />

								<div className="mt-6">
									<StatusButton
										className="mt-3 w-full"
										status={
											forgotPassword.state === 'submitting'
												? 'pending'
												: (form.status ?? 'idle')
										}
										type="submit"
										disabled={forgotPassword.state !== 'idle'}
									>
										Recover password
									</StatusButton>
								</div>
							</forgotPassword.Form>
							<div className="mt-6 flex items-center justify-center gap-2">
								<Link
									to="/login"
									className="mt-11 text-center text-body-sm font-bold"
								>
									Back to Login
								</Link>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	)
}

export function ErrorBoundary() {
	return <GeneralErrorBoundary />
}
